import Button from '@ui/buttons/button/button';
import Checkbox from '@ui/checkbox/checkbox';
import Container from '@ui/container/container';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';

import styles from './language-selector-option.module.css';
import useGetColorForProjectThemeComponents from '@/utils/colors/use-get-color-for-project-theme-components';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type TLanguageSelectorOptionProps = {
  data: TReactSelectOption;
  isFocused: boolean;
  isSelected: boolean;
  defaultLanguage: string;
  hideBaseSelector?: boolean;
  onBaseSelect?: (data: TReactSelectOption) => void;
};

const LanguageSelectorOption = ({
  onBaseSelect,
  data,
  defaultLanguage,
  hideBaseSelector,
  isSelected,
  isFocused,
}: TLanguageSelectorOptionProps) => {
  const isDefaultLanguage = data.value === defaultLanguage;
  const { hex } = useGetColorForProjectThemeComponents({
    colorVariable: EColorVariablesV2.primary,
    contrastAgainstAppBackground: {
      colorToChange: EColorVariablesV2.primary,
      colorToContrastAgainst: EColorVariablesV2.background,
    }, // pretty sure this is same as contrastAgainstAppBackground: true..
  });

  return (
    <Container height="100%" width="100%" className="flex jc-space-between ai-center position-relative cursor-pointer">
      <Checkbox inert value={data.value} checked={isSelected} />
      <img
        src={`${import.meta.env.VITE_ASSET_SERVER}/flags/round/${data.value}.svg`}
        alt={data.label}
        className="width-16 height-16 margin-l-4"
      />
      <Text design="GlobalIcoText2" className="S3 flex-1 margin-l-4" width="100%" clamp={1}>
        {data.label.toString().toUpperCase()}
      </Text>
      <OptionalComponent display={isDefaultLanguage}>
        <Text design="GlobalIcoText2" className="S3">
          BASE LANGUAGE
        </Text>
      </OptionalComponent>
      <OptionalComponent display={!isDefaultLanguage && isFocused && !hideBaseSelector}>
        <Button
          borderRadius="4px"
          className={`S3 padding-t-8 padding-b-12 padding-h-4 flex-column jc-center ${styles['select-base-language-button']}`}
          clear
          height="16px"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onBaseSelect?.(data);
          }}
        >
          <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.primary, EColorVariablesV2.background)}>
            Set as base
          </Text>
          <Container
            height="1px"
            width="100%"
            className={styles['select-base-language-button-underline']}
            border={`1px solid ${hex}`}
          />
        </Button>
      </OptionalComponent>
    </Container>
  );
};

export default LanguageSelectorOption;
